import React, { useState, useEffect } from "react";
import api from "../utils/api";

const formatCurrency = (value) => {
  const numericValue = parseFloat(value);
  if (typeof numericValue !== "number" || isNaN(numericValue)) return "R$ --";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);
};

const OfferTable = ({ onEditClick, refreshKey }) => {
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/ofertas");
      const fetchedOffers = response.data.map((offer) => ({
        ...offer,
        id: offer.id || offer._id,
        preco_original: parseFloat(offer.preco_original) || 0,
        preco_desconto: parseFloat(offer.preco_desconto) || 0,
      }));
      setOffers(fetchedOffers);
      setFilteredOffers(fetchedOffers);
      setSelectedIds([]); // Limpar seleção ao recarregar
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar ofertas:", error);
      setLoading(false);
      setOffers([]);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [refreshKey]);

  useEffect(() => {
    let result = offers;
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(
        (offer) =>
          offer.titulo.toLowerCase().includes(lowerTerm) ||
          offer.loja.toLowerCase().includes(lowerTerm)
      );
    }
    if (statusFilter !== "TODOS") {
      result = result.filter((offer) => offer.status === statusFilter);
    }
    setFilteredOffers(result);
    setSelectedIds([]); // Limpar seleção ao filtrar
  }, [searchTerm, statusFilter, offers]);

  const handleDelete = async (offerId) => {
    if (!window.confirm("Tem certeza que deseja DELETAR esta oferta?")) return;
    try {
      await api.delete(`/ofertas/${offerId}`);
      fetchOffers();
    } catch (error) {
      console.error("Erro ao deletar oferta:", error);
      alert("Erro ao deletar a oferta.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Deseja realmente DELETAR ${selectedIds.length} ofertas selecionadas?`)) return;

    try {
      await api.delete("/ofertas/bulk", { data: { ids: selectedIds } });
      fetchOffers();
      alert(`${selectedIds.length} ofertas deletadas com sucesso.`);
    } catch (error) {
      console.error("Erro na exclusão em massa:", error);
      alert("Erro ao deletar ofertas selecionadas.");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredOffers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredOffers.map(o => o.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  if (loading) return <p>Carregando ofertas...</p>;

  return (
    <div>
      <div className="filters-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por título ou loja..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="TODOS">Todos os Status</option>
            <option value="ATIVA">Ativas</option>
            <option value="INATIVA">Inativas</option>
          </select>
        </div>
        
        {selectedIds.length > 0 && (
          <button className="btn-primary" style={{ backgroundColor: '#dc3545', marginLeft: '15px' }} onClick={handleBulkDelete}>
            <i className="fas fa-trash"></i> Excluir Selecionados ({selectedIds.length})
          </button>
        )}
      </div>

      <div className="offer-table-container" style={{ boxShadow: 'none', padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input 
                  type="checkbox" 
                  checked={filteredOffers.length > 0 && selectedIds.length === filteredOffers.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Imagem</th>
              <th>Produto</th>
              <th>Loja</th>
              <th>Preço</th>
              <th>Status</th>
              <th>Data/Hora</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredOffers.map((offer) => (
              <tr key={offer.id} className={selectedIds.includes(offer.id) ? 'selected-row' : ''}>
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedIds.includes(offer.id)}
                    onChange={() => toggleSelect(offer.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>
                  <div className="product-thumb-container">
                    {offer.imagem_url ? (
                      <img 
                        src={offer.imagem_url} 
                        alt={offer.titulo} 
                        className="product-thumb-img"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/50?text=S/I';
                          e.target.onerror = null;
                        }}
                      />
                    ) : (
                      <i className="fas fa-image" style={{ color: '#ccc', fontSize: '1.2rem' }}></i>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ fontWeight: 'bold' }}>{offer.titulo}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>ID: {String(offer.id).substring(0, 6)}...</div>
                </td>
                <td>{offer.loja}</td>
                <td>
                  <div style={{ color: '#3483fa', fontWeight: 'bold' }}>{formatCurrency(offer.preco_desconto)}</div>
                  <div style={{ textDecoration: 'line-through', fontSize: '0.8rem', color: '#999' }}>{formatCurrency(offer.preco_original)}</div>
                </td>
                <td>
                  <span className={`badge ${offer.status === "ATIVA" ? "badge-active" : "badge-inactive"}`}>
                    {offer.status}
                  </span>
                </td>
                <td style={{ fontSize: '0.8rem', color: '#666', minWidth: '130px' }}>
                  <div title="Data de Cadastro">
                    <i className="fas fa-calendar-plus" style={{ width: '14px', textAlign: 'center', marginRight: '4px' }}></i>
                    {offer.createdAt ? new Date(offer.createdAt).toLocaleString('pt-BR') : (offer.data_cadastro ? new Date(offer.data_cadastro).toLocaleString('pt-BR') : '--')}
                  </div>
                  <div title="Última Atualização" style={{ marginTop: '4px' }}>
                    <i className="fas fa-edit" style={{ width: '14px', textAlign: 'center', marginRight: '4px' }}></i>
                    {offer.updatedAt ? new Date(offer.updatedAt).toLocaleString('pt-BR') : '--'}
                  </div>
                </td>
                <td className="actions">
                  <button className="btn-secondary" style={{ marginRight: '5px' }} onClick={() => onEditClick(offer)}>
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn-secondary" style={{ color: '#dc3545' }} onClick={() => handleDelete(offer.id)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredOffers.length === 0 && (
          <p style={{ textAlign: "center", padding: "20px", color: "#666" }}>
            Nenhuma oferta encontrada.
          </p>
        )}
      </div>
    </div>
  );
};

export default OfferTable;
